/**
 * Atlas — useIdleLogout
 *
 * Watches global user activity (pointer, keyboard, touch, scroll, focus)
 * and signs the user out after `timeoutMs` of no activity. Surfaces a
 * one-minute warning via `onWarn(secondsLeft)` so the host can render a
 * confirmation modal and let the user stay signed in.
 *
 * Activity events are bound at the document level with `passive: true`
 * and `capture: true` so they fire even when nested components stop
 * propagation. Listeners are removed on unmount.
 *
 * Skips entirely when `enabled` is false — pass `!!user` from the host
 * so we're not tracking activity on the login screen.
 *
 * timeoutMs is intentionally a number rather than a string so callers
 * can't accidentally pass an unvalidated config value; the public
 * AuthContext wrapper parses + clamps before instantiating.
 */
import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'touchstart',
  'scroll', 'wheel', 'click', 'focus',
];

// Cross-tab activity sync — typing in tab A shouldn't log out tab B.
// We piggyback on a localStorage key whose write triggers a `storage`
// event in every other tab of the same origin.
const ACTIVITY_BROADCAST_KEY = 'propatlas:lastActivity';

export function useIdleLogout({
  enabled,
  timeoutMs,
  warnMs = 60_000,
  onWarn,
  onLogout,
}) {
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  // Latest callbacks via refs so the effect doesn't re-bind on every
  // render of the host (which would reset the listeners + timers).
  const onWarnRef   = useRef(onWarn);
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onWarnRef.current   = onWarn;   }, [onWarn]);
  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);

  const clearTimers = useCallback(() => {
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
    if (warnTimer.current) { clearTimeout(warnTimer.current); warnTimer.current = null; }
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    if (!enabled || !timeoutMs) return;

    // Warning fires `warnMs` before the hard logout.
    const warnDelay = Math.max(0, timeoutMs - warnMs);
    warnTimer.current = setTimeout(() => {
      try { onWarnRef.current?.(Math.ceil(warnMs / 1000)); } catch (_) {}
    }, warnDelay);

    idleTimer.current = setTimeout(() => {
      try { onLogoutRef.current?.(); } catch (_) {}
    }, timeoutMs);
  }, [enabled, timeoutMs, warnMs, clearTimers]);

  useEffect(() => {
    if (!enabled || !timeoutMs) {
      clearTimers();
      return undefined;
    }

    reset();

    const localHandler = () => {
      // Broadcast our activity to other tabs so they reset too.
      try { localStorage.setItem(ACTIVITY_BROADCAST_KEY, String(Date.now())); } catch (_) {}
      reset();
    };

    const storageHandler = (e) => {
      if (e.key === ACTIVITY_BROADCAST_KEY) reset();
    };

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, localHandler, { passive: true, capture: true }),
    );
    window.addEventListener('storage', storageHandler);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, localHandler, { capture: true }),
      );
      window.removeEventListener('storage', storageHandler);
    };
  }, [enabled, timeoutMs, reset, clearTimers]);

  // Caller can force-reset when the user clicks "Stay signed in" in
  // the warning modal — same effect as any other activity.
  return { reset };
}
